import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import beautifyColumnKey from '../utils/beautifyColumnKey';
import ExpandablePanel from './ExpandablePanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TableExportButtons, useTableExport } from '../../../../components/common/table-export';
import { buildDiagnosticsExportFilename, getSelectedCsDeviceId } from '../../../../utils/export/diagnosticsExportFilename.js';

const MAX_VISIBLE_ROWS = 150;
const DEBOUNCE_MS = 250;

export default function DiagnosticsFixedColumnTable({
  title,
  columns,
  rows,
  searchValues,
  onColumnFilterChange,
  globalSearchValue,
  onGlobalSearchChange,
  tableId,
  emptyCell = '- -',
  fixedHeight = 420,
  exportSlug,
  getExportFilename,
  exportTableKey,
  csDiagnostics,
  allExportRows,
  extraHeaderActions,
}) {
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [matches, setMatches] = useState([]);
  const [debouncedFilter, setDebouncedFilter] = useState({});
  const cellRefs = useRef({});
  const debounceRef = useRef(null);

  useEffect(() => {
    cellRefs.current = {};
    setMatches([]);
    setCurrentMatchIndex(-1);
  }, [rows, globalSearchValue]);

  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedFilter(searchValues);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchValues]);

  const filteredRows = useMemo(
    () =>
      rows?.filter((row) =>
        columns.every((key) => {
          const filter = debouncedFilter[key];
          if (!filter) return true;
          const cell = row[key];
          const cellStr = cell !== null && cell !== undefined ? cell.toString().toLowerCase() : 'null';
          return cellStr.includes(filter);
        })
      ) ?? [],
    [rows, columns, debouncedFilter]
  );

  const visibleRows = useMemo(
    () => filteredRows.slice(0, MAX_VISIBLE_ROWS),
    [filteredRows]
  );

  const headerLabels = useMemo(() => columns.map((key) => beautifyColumnKey(key)), [columns]);

  const emptyForExport = emptyCell ?? '- -';

  const getExportFilenameBuilt = useCallback(
    (ext) =>
      buildDiagnosticsExportFilename({
        tableKey: exportTableKey,
        csId: getSelectedCsDeviceId(csDiagnostics),
        fromDate: csDiagnostics?.start_date,
        toDate: csDiagnostics?.end_date,
        extension: ext,
      }),
    [exportTableKey, csDiagnostics]
  );

  const effectiveGetExportFilename = getExportFilename ?? (exportTableKey ? getExportFilenameBuilt : undefined);

  const fullExportRows = allExportRows ?? rows;

  const getExportRows = useCallback(
    (scope) => {
      if (scope === 'current-page') return visibleRows;
      if (scope === 'filtered') return filteredRows;
      return fullExportRows;
    },
    [visibleRows, filteredRows, fullExportRows]
  );

  const getExportScopeCounts = useCallback(
    () => ({
      currentPage: visibleRows.length,
      filtered: filteredRows.length,
      all: fullExportRows.length,
    }),
    [visibleRows.length, filteredRows.length, fullExportRows.length]
  );

  const { exportCsv, exportExcel, getResolvedExportFilename, getExportScopeCounts: resolveScopeCounts, isExporting, canExport } = useTableExport({
    exportSlug,
    getExportFilename: effectiveGetExportFilename,
    columnKeys: columns,
    headerLabels,
    getExportRows,
    getExportScopeCounts,
    sheetTitle: title,
    emptyDisplay: emptyForExport,
  });

  const performSearch = (searchValue) => {
    if (!searchValue.trim()) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      // Clear highlights
      visibleRows.forEach((row, rowIndex) => {
        columns.forEach((key, colIndex) => {
          const targetCell = cellRefs.current[`${tableId}-${rowIndex}-${colIndex}`];
          if (targetCell) {
            const cellValue = row[key]?.toString() || '';
            targetCell.innerText = cellValue || emptyCell;
          }
        });
      });
      return;
    }

    const newMatches = [];
    const value = searchValue.toLowerCase();
    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedValue, 'gi');

    visibleRows.forEach((row, rowIndex) => {
      columns.forEach((key, colIndex) => {
        const cellValue = row[key]?.toString() || '';
        const targetCell = cellRefs.current[`${tableId}-${rowIndex}-${colIndex}`];
        if (targetCell) {
          if (cellValue.toLowerCase().includes(value)) {
            const highlightedHtml = cellValue.replace(regex, (match) => `<span class="bg-warning text-dark">${match}</span>`);
            targetCell.innerHTML = highlightedHtml;
            const cellMatches = [...targetCell.querySelectorAll('span.bg-warning')];
            cellMatches.forEach((match) => newMatches.push(match));
          } else {
            targetCell.innerText = cellValue || emptyCell;
          }
        }
      });
    });

    setMatches(newMatches);
    if (newMatches.length > 0) {
      setCurrentMatchIndex(0);
      newMatches.forEach((match, index) => {
        match.classList.toggle('bg-danger', index === 0);
        match.classList.toggle('text-white', index === 0);
      });
      newMatches[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    } else {
      setCurrentMatchIndex(-1);
    }
  };

  const navigateMatch = (direction) => {
    if (matches.length === 0) return;
    let newIndex = direction === 'next' ? currentMatchIndex + 1 : currentMatchIndex - 1;
    if (newIndex >= matches.length) newIndex = 0;
    if (newIndex < 0) newIndex = matches.length - 1;

    setCurrentMatchIndex(newIndex);
    matches.forEach((match, index) => {
      const isCurrent = index === newIndex;
      match.classList.toggle('bg-danger', isCurrent);
      match.classList.toggle('text-white', isCurrent);
      match.classList.toggle('bg-warning', !isCurrent);
      match.classList.toggle('text-dark', !isCurrent);
    });
    matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  };

  const clearSearch = () => {
    onGlobalSearchChange({ target: { value: '' } });
    performSearch('');
  };

  const onGlobalSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (matches.length > 0) {
        navigateMatch('next');
      } else {
        performSearch(globalSearchValue);
      }
    } else if (e.key === 'Escape') {
      clearSearch();
    }
  };

  const rowCountLabel = filteredRows.length > MAX_VISIBLE_ROWS
    ? `${MAX_VISIBLE_ROWS} of ${filteredRows.length} rows`
    : `${filteredRows.length} rows`;

  const headerActions = (
    <div className="d-flex align-items-center gap-2 cs-panel__toolbar">
      <span className="cs-row-count">{rowCountLabel}</span>
      <div className="cs-global-search-container">
        <input
          id={`${tableId}-global-search`}
          placeholder="Search"
          onChange={onGlobalSearchChange}
          onKeyDown={onGlobalSearchKeyDown}
          value={globalSearchValue}
          className="s-round-input"
          type="text"
          aria-label={`${title} global search`}
        />
        {globalSearchValue && (
          <div className="cs-search-nav">
            <span className="cs-search-count">
              {matches.length > 0 ? `${currentMatchIndex + 1}/${matches.length}` : '0/0'}
            </span>
            <button
              type="button"
              className="cs-search-arrow"
              onClick={() => navigateMatch('prev')}
              disabled={matches.length === 0}
              title="Previous match"
            >
              <FontAwesomeIcon icon={faChevronUp} />
            </button>
            <button
              type="button"
              className="cs-search-arrow"
              onClick={() => navigateMatch('next')}
              disabled={matches.length === 0}
              title="Next match"
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
            <button
              type="button"
              className="cs-search-clear"
              onClick={clearSearch}
              title="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
      </div>
      {extraHeaderActions}
      {getExportFilename || exportTableKey || exportSlug ? (
        <TableExportButtons
          canExport={canExport}
          isExporting={isExporting}
          onCsv={exportCsv}
          onExcel={exportExcel}
          getResolvedExportFilename={getResolvedExportFilename}
          getExportScopeCounts={resolveScopeCounts}
        />
      ) : null}
    </div>
  );

  return (
    <ExpandablePanel title={title} fixedHeight={fixedHeight} headerActions={headerActions}>
      <table
        id={tableId}
        className="table align-top table-sm cs-table cs-table--sticky-layout"
      >
        <thead className="table-light">
          <tr>
            {columns.map((key) => (
              <th key={key} scope="col" style={{ minWidth: '100px' }}>
                {beautifyColumnKey(key)}
              </th>
            ))}
          </tr>
          <tr>
            {columns.map((key) => (
              <th key={`f-${key}`}>
                <input
                  type="text"
                  className="form-control form-control-sm font-size-12"
                  placeholder={`Search ${key}`}
                  onChange={(e) => onColumnFilterChange(e, key)}
                  value={searchValues[key] || ''}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.length > 0 ? (
            visibleRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((key, colIndex) => (
                  <td
                    key={key}
                    ref={(el) => {
                      cellRefs.current[`${tableId}-${rowIndex}-${colIndex}`] = el;
                    }}
                    style={{ maxWidth: '350px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                  >
                    {row[key] != null && row[key] !== '' ? row[key] : emptyCell ?? '- -'}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={Math.max(columns.length, 1)} className="text-center py-4">
                No matching records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </ExpandablePanel>
  );
}
